import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "../base/private/privateBaseQuery";
import { ApiEnvelope, UserEntity } from "@/types/canonical";

export type UpdateMePayload = {
  id: number;
  school_id: number | null;
  auth_type: null;
  group: null;
  photo: string | null;
  photo_thumb: Record<string, unknown>;
  color: string | null;
  firstname: string | null;
  lastname: string | null;
  patronymic: null;
  email: string | null;
  phone: string | null;
  username: string | null;
  description: string | null;
  password: string | null;
  school: Record<string, unknown>;
  register_date: string | null;
  last_activity_date: string | null;
  additional: Record<string, unknown>;
  is_online: boolean;
  is_need_complete_challenge: boolean;
  is_multiple_schools: boolean;
};

/** Универсальный анвраппер {status, status_code, entity} -> entity */
const unwrap = <T>(res: T | ApiEnvelope<T>): T => {
  const maybe = res as ApiEnvelope<T>;
  return typeof maybe === "object" && maybe !== null && "entity" in maybe
    ? (maybe.entity as T)
    : (res as T);
};

export const authPrivateApi = createApi({
  reducerPath: "authPrivateApi",
  baseQuery: privateBaseQuery(),
  endpoints: (builder) => ({
    getUser: builder.query<UserEntity, void>({
      query: () => ({
        url: "/user.json?extended=1",
        method: "GET",
        headers: {
          "X-Requested-Fields":
            "id,school_id,firstname,lastname,patronymic,username,email,phone,photo,photo_thumb,color,description,group,school,school:languages,school:settings,register_date,last_activity_date,additional,is_online,is_need_complete_challenge,is_multiple_schools,status,teacher,student,external_id",
        },
      }),
      transformResponse: (res: ApiEnvelope<UserEntity> | UserEntity) =>
        unwrap<UserEntity>(res),
    }),

    updateMe: builder.mutation<UserEntity, UpdateMePayload>({
      query: (data) => ({
        url: "/user.json",
        method: "PUT",
        data,
        headers: {
          "X-Requested-Fields":
            "id,school_id,firstname,lastname,photo,email,phone,additional,password",
        },
      }),
      transformResponse: (res: ApiEnvelope<UserEntity> | UserEntity) =>
        unwrap<UserEntity>(res),
    }),

    checkAuth: builder.query<Record<string, unknown>, void>({
      query: () => ({
        url: "/auth/check",
        method: "GET",
      }),
      transformResponse: (
        res: ApiEnvelope<Record<string, unknown>> | Record<string, unknown>
      ) => unwrap<Record<string, unknown>>(res),
    }),

    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          localStorage.clear();

          if (typeof window !== "undefined") {
            // storage event does NOT fire in the same tab
            window.dispatchEvent(new Event("auth-token-changed"));
            // Reset RTK Query cache to clear user data
            dispatch(authPrivateApi.util.resetApiState());
            window.location.href = "/auth";
          }
        } catch {
          localStorage.clear();

          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event("auth-token-changed"));
            // Reset RTK Query cache to clear user data
            dispatch(authPrivateApi.util.resetApiState());
          }
        }
      },
    }),
  }),
});

export const {
  useGetUserQuery,
  useUpdateMeMutation,
  useCheckAuthQuery,
  useLogoutMutation,
} = authPrivateApi;
